import { CREATE_SESSION, END_SESSION, GET_RESPONSE } from '../graphql/mutations';
import { GET_SESSION } from '../graphql/queries';
;
export class CopilotEngine {
    constructor(client) {
        this.visibilityChangeHandler = null;
        this.abortController = null;
        this.isStreaming = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 3;
        this.client = client;
        this.setupVisibilityHandling();
    }
    // Setup handling for page visibility changes (tab switching)
    setupVisibilityHandling() {
        this.visibilityChangeHandler = () => {
            if (document.hidden && this.isStreaming) {
                console.log('Page became hidden, maintaining streaming connection...');
                // Keep the connection alive but log the state change
            }
            else if (!document.hidden && this.isStreaming) {
                console.log('Page became visible, streaming connection active...');
            }
        };
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        }
    }
    // Cleanup method to remove event listeners
    cleanup() {
        if (this.visibilityChangeHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        }
        this.stopStreaming();
    }
    // Start Session
    async startSession(orgId, userId, sessionVariables) {
        try {
            const response = await this.client.mutate({
                mutation: CREATE_SESSION,
                variables: {
                    organizationId: orgId,
                    anonymousUserId: userId || null,
                    sessionVariables: sessionVariables,
                },
            });
            //console.log('Raw response in Startr Session:', response);
            return response;
        }
        catch (error) {
            console.error('Error starting session', error);
            return null;
        }
    }
    // Get Response
    async getResponse(sessionId, message) {
        try {
            const response = await this.client.mutate({
                mutation: GET_RESPONSE,
                variables: {
                    sessionId,
                    message,
                },
            });
            return response;
        }
        catch (error) {
            console.error('Error getting response', error);
            return null;
        }
    }
    async getStreamingResponse(sessionId, user_response, onMessage) {
        return this.streamWithRetry(sessionId, user_response, onMessage);
    }
    async streamWithRetry(sessionId, user_response, onMessage) {
        // Cancel any existing request
        this.stopStreaming();
        // Create new abort controller for this request
        this.abortController = new AbortController();
        this.isStreaming = true;
        try {
            const response = await fetch(`${process.env.BACKEND_STREAMING_URL}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `JWT ${localStorage.getItem('accessToken')}`,
                },
                body: JSON.stringify({ session_id: sessionId, ...user_response }),
                signal: this.abortController.signal,
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            if (!response.body) {
                throw new Error('Response body is null');
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        break;
                    }
                    // Decode the chunk and add to buffer
                    buffer += decoder.decode(value, { stream: true });
                    // Split by lines to handle multiple events in one chunk
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // Keep the last incomplete line in buffer
                    for (const line of lines) {
                        await this.processStreamLine(line.trim(), onMessage);
                    }
                }
                // Process any remaining data in buffer
                if (buffer.trim()) {
                    await this.processStreamLine(buffer.trim(), onMessage);
                }
            }
            finally {
                reader.releaseLock();
            }
        }
        catch (error) {
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    console.log('Streaming request was aborted');
                    return;
                }
                // Handle network errors and potential reconnection
                if (this.shouldRetry(error) && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
                    // Wait before retrying (exponential backoff)
                    await this.delay(Math.pow(2, this.reconnectAttempts) * 1000);
                    if (this.isStreaming) {
                        return this.streamWithRetry(sessionId, user_response, onMessage);
                    }
                }
                console.error('Error getting streaming response:', error.message);
                onMessage({ type: 'error', data: { token: `Error: ${error.message}` } });
            }
            else {
                console.error('Unknown error getting streaming response:', error);
                onMessage({ type: 'error', data: { token: 'Unknown error occurred' } });
            }
        }
        finally {
            this.isStreaming = false;
            this.abortController = null;
            this.reconnectAttempts = 0;
        }
    }
    async processStreamLine(line, onMessage) {
        if (!line || line.startsWith(':')) {
            // Skip comments and empty lines
            return;
        }
        try {
            let eventData = '';
            // Handle Server-Sent Events format (data: {...})
            if (line.startsWith('data: ')) {
                eventData = line.slice(6); // Remove 'data: ' prefix
            }
            else {
                // Handle raw JSON
                eventData = line;
            }
            // Skip heartbeat messages
            if (eventData === '[DONE]' || eventData === 'ping' || !eventData.trim()) {
                return;
            }
            console.log('Server Response Message: ', eventData);
            let data = JSON.parse(eventData);
            if (typeof data == 'string') {
                data = JSON.parse(data);
            }
            console.log('Parsed Data: ', data);
            if (data.token !== undefined) {
                onMessage({ type: 'token', data: { token: data.token, tooltip: data.tooltip, is_last: data.is_last } });
            }
            else if (data.form) {
                onMessage({ type: 'form', data: { form: data.form } });
            }
            else if (data.suggestions) {
                onMessage({ type: 'suggestions', data: { suggestions: data.suggestions } });
            }
            else if (data.buttons) {
                onMessage({ type: 'buttons', data: { buttons: data.buttons } });
            }
            else if (data.api_call) {
                console.log('API Call: ', data.api_call);
                onMessage({ type: 'api_call', data: { api_call: data.api_call } });
            }
            else if (data.tags) {
                console.log('Tags: ', data.tags);
                onMessage({ type: 'tags', data: { tags: data.tags } });
            }
            else if (data.k_cards) {
                console.log('Feature Cards: ', data.feature_cards);
                onMessage({ type: 'k_cards', data: { k_cards: data.k_cards } });
            }
            else if (data.uploader) {
                console.log('Uploader: ', data.uploader);
                onMessage({ type: 'uploader', data: { uploader: data.uploader } });
            }
            else if (data.batch) {
                console.log('Batch: ', data.batch);
                onMessage({ type: 'batch', data: { batch: data.batch } });
            }
            else if (data.blueprint) {
                console.log('Blueprint: ', data.blueprint);
                onMessage({ type: 'blueprint', data: { blueprint: data.blueprint } });
            }
            else if (data.loader) {
                console.log('Loader: ', data.loader);
                onMessage({ type: 'loader', data: { loader: data.loader } });
            }
            else {
                onMessage({ type: 'error', data: { token: 'Invalid response format' } });
            }
        }
        catch (parseError) {
            console.error('Error parsing event data:', parseError, 'Raw line:', line);
            // onMessage({ type: 'error', data: { token: 'Error parsing response data' } });
        }
    }
    // Method to stop streaming
    stopStreaming() {
        this.isStreaming = false;
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }
    // Helper method to determine if we should retry based on the error
    shouldRetry(error) {
        // Retry on network errors, timeouts, and connection issues
        return (error.name === 'TypeError' || // Network error
            error.name === 'TimeoutError' ||
            error.message.includes('fetch') ||
            error.message.includes('network') ||
            error.message.includes('connection') ||
            error.message.includes('Failed to fetch'));
    }
    // Helper method to add delay for retry logic
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    // Method to check if currently streaming
    isCurrentlyStreaming() {
        return this.isStreaming;
    }
    async getSuggestions(sessionId) {
        try {
            const response = await fetch(`${process.env.BACKEND_SUGGESTIONS_URL}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `JWT ${localStorage.getItem('accessToken')}`,
                },
                body: JSON.stringify({ session_id: sessionId }),
            });
            const data = await response.json();
            return data.suggestions;
        }
        catch (error) {
            console.error('Error getting suggestions', error);
            return null;
        }
    }
    async getSession(sessionId) {
        try {
            const response = await this.client.query({
                query: GET_SESSION,
                variables: { sessionId: sessionId },
            });
            const session = response.data.allSessions.edges[0].node;
            console.log('Session: ', session);
            return session;
        }
        catch (error) {
            console.error('Error getting session', error);
            return null;
        }
    }
    // End Session
    async endSession(sessionId) {
        try {
            // Stop any ongoing streaming connections
            this.stopStreaming();
            // Reset internal state
            this.reconnectAttempts = 0;
            // End the session on the backend
            const response = await this.client.mutate({
                mutation: END_SESSION,
                variables: {
                    sessionId,
                },
            });
            console.log('Session ended successfully:', sessionId);
            return response;
        }
        catch (error) {
            console.error('Error ending session', error);
            return null;
        }
    }
}
